<template>
  <div id="account-details" class="section">

    <div class="space"></div>

    <h2 class="section-header" v-show="!selectedOption || !isSmallScreen">{{ $t('account.acctDetails.title') }}</h2>

    <b-row class="justify-content-center">
      <!-- In small screens both sidebar and content take whole width, but they switch its visibility with ShowSidebar-->
      <!-- In larger secreens, sidebar is always visible  -->
      <b-col id="sidebar" cols="12" lg="4" xl="3" v-show="showSidebar || !isSmallScreen">
        <ul class="acct-sidebar">
          <!-- ORDERS-->
          <li class="acct-sidebar-item" @click="gotoMyOrders">
            <font-awesome-icon :icon="['fas', 'box-open']" size="lg"/>
            &nbsp;
            <span>{{ $t('account.acctDetails.myOrders') }}</span>
          </li>
          <!-- PERSONAL INFO -->
          <li
            class="acct-sidebar-item"
            :class="{'acct-sidebar-active-item': showPersonalInfo}"
            @click="gotoPersonalInfo"
          >
            <font-awesome-icon :icon="['fas', 'user-cog']" size="lg"/>
            &nbsp;
            <span>{{ $t('account.acctDetails.personalInfo') }}</span>
          </li>
          <!-- LOGIN & SECURITY -->
          <li
            class="acct-sidebar-item"
            :class="{'acct-sidebar-active-item': showLoginAndSecurity}"
            @click="gotoLoginAndSecurity"
          >
            <font-awesome-icon :icon="['fas', 'shield-alt']" size="lg"/>
            &nbsp;
            <span>{{ $t('account.acctDetails.security') }}</span>
          </li>
          <!-- ADDRESSES -->
          <li
            class="acct-sidebar-item"
            :class="{'acct-sidebar-active-item': showAddressBook}"
            @click="gotoAddressBook"
          >
            <font-awesome-icon :icon="['fas', 'map-marked-alt']" size="lg"/>
            &nbsp;
            <span>{{ $t('account.acctDetails.addresses') }}</span>
          </li>
        </ul>
      </b-col>

      <!-- In larger secreens, content is always visible  -->
      <b-col id="content" cols="12" lg="8" xl="9" v-show="!showSidebar || !isSmallScreen">
        <transition name="cardMenu-transition" enter-active-class="animated fadeInRight faster">
          <!-- In larger screens by default shows a dashboard(cards menu like), and switch its visibilty with the other componnets -->
          <!-- In small screens dashboard is not even charged to DOM -->
          <div class="cards-container" v-show="showOptionCards && !selectedOption" v-if="!isSmallScreen" key="cards-container">
            <b-card no-body class="col-5 option-card" @click="gotoMyOrders">
              <div class="option-card-body">
                <font-awesome-icon :icon="['fas', 'box-open']" size="2x"/>
                <br>
                <span>{{ $t('account.acctDetails.myOrders') }}</span>
              </div>
            </b-card>
            
            <b-card no-body class="col-5 option-card" @click="gotoPersonalInfo">
              <div class="option-card-body">
                <font-awesome-icon :icon="['fas', 'user-cog']" size="2x"/>
                <br>
                <span>{{ $t('account.acctDetails.personalInfo') }}</span>
              </div>
            </b-card>

            <b-card no-body class="col-5 option-card" @click="gotoLoginAndSecurity">
              <div class="option-card-body">
                <font-awesome-icon :icon="['fas', 'shield-alt']" size="2x"/>
                <br>
                <span>{{ $t('account.acctDetails.security') }}</span>
              </div>
            </b-card>
            
            <b-card no-body class="col-5 option-card" @click="gotoAddressBook">
              <div class="option-card-body">
                <font-awesome-icon :icon="['fas', 'map-marked-alt']" size="2x"/>
                <br>
                <span>{{ $t('account.acctDetails.addresses') }}</span>
              </div>
            </b-card>
          </div>
        </transition>

        <transition-group name="sections-transition" enter-active-class="animated fadeInRight faster">
          <personal-info v-if="showPersonalInfo" @hide="hidePersonalInfo" key="personal-info"/>
          <login-and-security v-if="showLoginAndSecurity" @hide="hideLoginAndSecurity" key="login-and-security"/>
          <address-book v-if="showAddressBook" @hide="hideAddressBook" key="address-book"/>
        </transition-group>
      </b-col>
    </b-row>
    
  </div>
</template>

<script>
import ScreenMixin from '../../utils/ScreenMixin';
import PersonalInfo from './PersonalInfo.vue';
import LoginAndSecurity from './LoginAndSecurity.vue';
import AddressBook from './AddressBook.vue';

export default {
  name: 'AccountDetails',

  components: {
    PersonalInfo,
    LoginAndSecurity,
    AddressBook
  },

  data() {
    return {
      showSidebar: true,
      showOptionCards: true,
      selectedOption: null,
      showPersonalInfo: false,
      showLoginAndSecurity: false,
      showAddressBook: false,
    }
  },
  
  mixins: [ScreenMixin],

  async created() {
    await this.$store.dispatch('accountStore/getAccountData');
  },

  computed: {
    isShowSideBar() {
      return this.isSmallScreen && this.selectedOption === null;
    },

    isShowOptionsCards() {
      return !this.isSmallScreen && this.selectedOption === null;
    }
  },

  watch: {
    isShowSideBar(newValue) {
      this.showSidebar = newValue;
    },
    
    isShowOptionsCards(newValue) {
      this.showOptionCards = newValue;
    }
  },

  methods: {
    // Switch desktop main content
    switchMainContent() {
      // Switch the inside of content between sections/components
      if(!this.isSmallScreen && this.selectedOption !== null) {
        this.$data[this.selectedOption] = false; // Hide the actual component
      }
    },

    gotoMyOrders() {
      this.$router.push('/orders');
    },

    gotoPersonalInfo() {
      const personalInfoFlag = 'showPersonalInfo';

      // Move to personal info section only if user its not in this section
      if (this.selectedOption !== personalInfoFlag) {
        this.switchMainContent();
        this.showPersonalInfo = true;
        this.selectedOption = personalInfoFlag;
      }
    },

    hidePersonalInfo() {
      this.selectedOption = null;
      this.showPersonalInfo = false;
      this.switchMainContent();
    },

    gotoLoginAndSecurity() {
      const loginAndSecurityFlag = 'showLoginAndSecurity';

      // Move to login and security section only if user its not in this section
      if (this.selectedOption !== loginAndSecurityFlag) {
        this.switchMainContent();
        this.showLoginAndSecurity = true;
        this.selectedOption = loginAndSecurityFlag;
      }
    },

    hideLoginAndSecurity() {
      this.selectedOption = null;
      this.showLoginAndSecurity = false;
      this.switchMainContent();
    },

    gotoAddressBook() {
      const addressBookFlag = 'showAddressBook';

      // Move to address book section only if user its not in this section
      if (this.selectedOption !== addressBookFlag) {
        this.switchMainContent();
        this.showAddressBook = true;
        this.selectedOption = addressBookFlag;
      }
    },

    hideAddressBook() {
      this.selectedOption = null;
      this.showAddressBook = false;
      this.switchMainContent();
    }
  }
};
</script>

<style lang="scss">
  @import '../../assets/css/constants.scss';

  #content {
    @media (min-width: 992px) {
      background-color: white;
      border-radius: 6px;
      border: 2px solid rgba(0, 0, 0, 0.055);
    }
  }

  .acct-sidebar{
    text-align: left;
  }

  .acct-sidebar-item{
    padding: 0.8rem 1rem;
    font-size: 1rem;
  }

  .acct-sidebar-item:hover{
    background-color: $secondary-hover-bgColor;
    border-radius: 5px;
    cursor: pointer;
  }

  .acct-sidebar-active-item {
    background-color: $secondary-hover-bgColor;
    border-radius: 5px;
    color: $secondary-hover-textColor;
    cursor: pointer;
  }

  .cards-container{
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-around;

    padding-top: 3rem;
  }

  .option-card{
    margin-bottom: 3rem;
    height: 12rem;
    span {
      font-size: 1.1rem;
    }
  }

  .option-card:hover{
    background-color: $secondary-hover-bgColor;
    color: $secondary-hover-textColor;
    border-color: $white-shade;
    cursor: pointer;
  }

  .option-card-body{
    display: flex;
    -ms-flex: 1 1 auto;
    -webkit-box-flex: 1;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 1px;
    padding: 1.25rem;
  }
  
  .acct-back-btn{
    margin: 2em 0 0 2em;
    border-color: #dbdbdb !important;
  }
</style>
